import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { Box } from "@similarweb/ui-components/foundations";

import FiltersListLoader from "../FiltersListLoader/FiltersListLoader";

type DeferredFilterContentProps = {
    isLoading?: boolean;
    children: ReactNode;
};

// Only show the deferral skeleton if the content takes longer than a beat to mount, so fast opens
// resolve before the timer and never flash a loader. Slow opens get a "loading" affordance instead
// of a visibly empty overlay.
const SKELETON_DELAY_MS = 200;

/**
 * Keeps the "Show more filters" click responsive. Mounting the full categorized filter list (~9
 * sections, dozens of controls) synchronously inside the click handler blocks the interaction for
 * seconds on low-end devices. Instead the overlay shell paints first, then the heavy children mount.
 *
 * The mount is deferred behind a double requestAnimationFrame rather than a transition: under heavy
 * main-thread load React can complete a transition within the same task (no intervening paint), so a
 * paint boundary is needed to guarantee the shell is presented before the costly content lays out.
 *
 * During the deferral we render nothing for the first ~200ms (so warm opens don't flash), then a
 * skeleton if the content is still mounting. The skeleton is also used for a genuine data-loading
 * state (`isLoading`).
 */
export const DeferredFilterContent = ({ isLoading, children }: DeferredFilterContentProps) => {
    const [isContentReady, setIsContentReady] = useState(false);
    const [showDeferralSkeleton, setShowDeferralSkeleton] = useState(false);

    useEffect(() => {
        let innerFrame = 0;

        const skeletonTimer = setTimeout(() => setShowDeferralSkeleton(true), SKELETON_DELAY_MS);

        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                clearTimeout(skeletonTimer);
                setIsContentReady(true);
            });
        });

        return () => {
            clearTimeout(skeletonTimer);
            cancelAnimationFrame(outerFrame);
            cancelAnimationFrame(innerFrame);
        };
    }, []);

    if (isLoading || (!isContentReady && showDeferralSkeleton)) {
        return (
            <Box padding="16px">
                <FiltersListLoader rowNumber={6} />
            </Box>
        );
    }

    if (!isContentReady) {
        return null;
    }

    return children;
};
